import React from 'react';
import {
  IconButton,
  Stack,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Theme,
  Divider,
  Typography,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Event as ScheduleIcon,
  Campaign as PromotionIcon,
  CalendarMonth,
  RocketLaunch,
  Science,
  Sync as SyncIcon,
} from '@mui/icons-material';
import { OfferListItem } from '../../types';
import { Module } from '@modules/admin/types';
import { usePermission } from '@modules/auth/hooks';

import OfferPreviewDialog from '../preview/OfferPreviewDialog';
import { ScheduleDrawer } from '../Drawers/ScheduleDrawer';
import { PromotionDrawer } from '../Drawers/PromotionDrawer';
import ScheduleLogsDialog from './ScheduleLogsDialog';
import SyncLogsDialog from './SyncLogsDialog';
import { useDialog } from '@shared/hooks/useDialog';

interface ActionButtonsProps {
  row: OfferListItem;
  navigateToOffer: (path: string) => void;
  handleStatusToggle: (row: OfferListItem, targetStatus: string) => void;
  theme: Theme;
}

const ActionButtons: React.FC<ActionButtonsProps> = ({ row, navigateToOffer, handleStatusToggle, theme }) => {
  // Get permissions for offer module
  const { canUpdate } = usePermission(Module.OFFER);

  // Menu state
  const menu = useDialog();
  const anchorEl = React.useRef<HTMLElement | null>(null);

  // Dialog states using useDialog hook
  const logsDialog = useDialog();
  const syncLogsDialog = useDialog();
  const previewDialog = useDialog();
  const scheduleDrawer = useDialog();
  const promotionDrawer = useDialog();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    anchorEl.current = event.currentTarget;
    menu.handleOpen();
  };

  const handleMenuClose = (event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
    }
    menu.handleClose();
  };

  const handlePushToSandbox = () => {
    handleStatusToggle(row, 'staging');
    handleMenuClose();
  };

  const handlePushToProduction = () => {
    handleStatusToggle(row, 'prod');
    handleMenuClose();
  };

  const handleOpenLogs = () => {
    logsDialog.handleOpen();
    handleMenuClose();
  };

  const handleOpenSyncLogs = () => {
    syncLogsDialog.handleOpen();
    handleMenuClose();
  };

  const handleOpenPreview = () => {
    previewDialog.handleOpen();
    handleMenuClose();
  };

  const handleOpenSchedule = () => {
    scheduleDrawer.handleOpen();
    handleMenuClose();
  };

  const handleOpenPromotion = () => {
    promotionDrawer.handleOpen();
    handleMenuClose();
  };

  const isDraft = row.sync_on === 'draft';
  const isSandbox = row.sync_on === 'staging';
  const isProd = row.sync_on === 'prod';

  // Determine which push options to show
  const showPushToSandbox = isDraft || isProd;
  const showPushToProduction = isDraft || isSandbox;

  return (
    <Stack direction="row" spacing={1} justifyContent="center">
      <IconButton
        size="small"
        aria-controls={menu.open ? 'action-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={menu.open ? 'true' : undefined}
        onClick={handleMenuOpen}
        sx={{
          color: theme.palette.text.secondary,
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
            color: theme.palette.text.primary,
          },
        }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>

      <Menu
        id="action-menu"
        anchorEl={anchorEl.current}
        open={menu.open}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              minWidth: 200,
              boxShadow: theme.shadows[5],
              borderRadius: 1.5,
              overflow: 'visible',
            },
          },
        }}
      >
        {/* --- Group 1: View Actions --- */}
        <Typography
          variant="caption"
          sx={{
            px: 2,
            py: 0.5,
            fontWeight: 600,
            color: theme.palette.text.secondary,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}
        >
          View
        </Typography>

        <MenuItem
          onClick={() => {
            navigateToOffer(`/offers/${row.offer_id}`);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <ViewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Offer Details</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleOpenPreview}>
          <ListItemIcon>
            <ViewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Preview</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleOpenLogs}>
          <ListItemIcon>
            <CalendarMonth fontSize="small" />
          </ListItemIcon>
          <ListItemText>Audit Trail Logs</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleOpenSyncLogs}>
          <ListItemIcon>
            <SyncIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Sync Logs</ListItemText>
        </MenuItem>

        {/* --- Group 2: Edit Actions --- */}
        {canUpdate && <Divider sx={{ my: 0.5 }} />}

        {canUpdate && (
          <Typography
            variant="caption"
            sx={{
              px: 2,
              py: 0.5,
              fontWeight: 600,
              color: theme.palette.text.secondary,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
            }}
          >
            Edit
          </Typography>
        )}

        {canUpdate && (
          <MenuItem
            onClick={() => {
              navigateToOffer(`/offers/${row.offer_id}/edit`);
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit Offer</ListItemText>
          </MenuItem>
        )}

        {/* Sync to Sandbox */}
        {canUpdate && showPushToSandbox && (
          <MenuItem onClick={handlePushToSandbox} sx={{ color: theme.palette.primary.main }}>
            <ListItemIcon>
              <Science fontSize="small" sx={{ color: theme.palette.primary.main }} />
            </ListItemIcon>
            <ListItemText>Sync to Sandbox</ListItemText>
          </MenuItem>
        )}

        {/* Sync to Production */}
        {canUpdate && showPushToProduction && (
          <MenuItem onClick={handlePushToProduction} sx={{ color: theme.palette.success.main }}>
            <ListItemIcon>
              <RocketLaunch fontSize="small" sx={{ color: theme.palette.success.main }} />
            </ListItemIcon>
            <ListItemText>Sync to Production</ListItemText>
          </MenuItem>
        )}

        {/* --- Group 3: Configuration --- */}
        {canUpdate && <Divider sx={{ my: 0.5 }} />}

        {canUpdate && (
          <Typography
            variant="caption"
            sx={{
              px: 2,
              py: 0.5,
              fontWeight: 600,
              color: theme.palette.text.secondary,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
            }}
          >
            Configuration
          </Typography>
        )}

        {canUpdate && (
          <MenuItem onClick={handleOpenSchedule}>
            <ListItemIcon>
              <ScheduleIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Configure Schedule</ListItemText>
          </MenuItem>
        )}

        {canUpdate && (
          <MenuItem onClick={handleOpenPromotion}>
            <ListItemIcon>
              <PromotionIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Configure Promotion</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {logsDialog.open && (
        <ScheduleLogsDialog open={logsDialog.open} offerId={row.offer_id} onClose={logsDialog.handleClose} />
      )}
      {syncLogsDialog.open && (
        <SyncLogsDialog open={syncLogsDialog.open} offerId={row.offer_id} onClose={syncLogsDialog.handleClose} />
      )}
      {previewDialog.open && (
        <OfferPreviewDialog open={previewDialog.open} offerId={row.offer_id} onClose={previewDialog.handleClose} />
      )}

      {/* Schedule Drawer */}
      <ScheduleDrawer
        open={scheduleDrawer.open}
        onClose={scheduleDrawer.handleClose}
        offer={row}
        initialSchedule={row.offer_schedule?.[0] ?? null}
      />

      {/* Promotion Drawer */}
      <PromotionDrawer
        open={promotionDrawer.open}
        onClose={promotionDrawer.handleClose}
        offer={row}
        initialPromotion={row.offer_promotion?.[0] ?? null}
      />
    </Stack>
  );
};

export default ActionButtons;
